import * as Api from '@ViewModels';
import * as React from 'react';
import { findPlatformCapabilities } from '../../../../models/utils/platformCapabilities';
import {
	customizationDiffersFromBase,
	findPlatformCustomization,
	getActivePlatform,
	getPlatformVersionChanges,
	getTargetedPlatforms,
	isMediaLeftOffAPlatform,
} from '../../../../models/utils/platformCustomizations';
import {
	getErrorsForPlatform,
	getPostWideErrors,
	getSaveBlockingErrors,
	validateSocialPostDraft,
} from '../../../../models/utils/socialPostValidation';
import { usePlatformCapabilitiesQuery } from '../../../../queries/Social/usePlatformCapabilitiesQuery';
import { IEditSocialMediaPostContext } from '../../../containers/socialMedia/EditSocialMediaPost/context';

export interface IUsePlatformCustomizationOptions {
	/** Passed in rather than read from React context, so a caller outside the provider can use it too. */
	context: IEditSocialMediaPostContext;
	/** Off for surfaces that only ever author one body, such as the admin template editors. */
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
}

/**
 * Everything the composer needs to author one version per platform: which version is on screen,
 * the limits it is held to, and the errors it currently has.
 *
 * Every figure here comes from GET social/platformCapabilities. Until it answers, `capabilities`
 * and `characterLimit` are undefined and the limit checks stay quiet — the composer shows no
 * ceiling rather than one it made up, and the save path remains the authority either way.
 */
export function usePlatformCustomization({
	context,
	enabled = true,
	impersonationContext,
}: IUsePlatformCustomizationOptions) {
	const {
		activePlatform: selectedPlatform,
		customizeByPlatform,
		platformCustomizations,
		postContent,
		postImages,
		postTargets,
		resetPlatformCustomization,
		setActivePlatform,
		setCustomizeByPlatform,
		setPlatformCustomization,
	} = context;

	const targetedPlatforms = React.useMemo(() => getTargetedPlatforms(postTargets), [postTargets]);
	const capabilitiesQuery = usePlatformCapabilitiesQuery({
		enabled,
		impersonationContext,
		platforms: targetedPlatforms,
	});
	const capabilities = capabilitiesQuery.data;

	const isSplit = !!customizeByPlatform && targetedPlatforms.length > 0;
	// The same rule the composer context applies when it routes newly added media, so the version a
	// tab shows and the version an upload lands on are always the same one.
	const activePlatform = getActivePlatform(postTargets, isSplit ? selectedPlatform : undefined);

	const errors = React.useMemo(
		() =>
			validateSocialPostDraft({
				baseAttachments: postImages,
				baseContent: postContent,
				capabilities,
				customizations: platformCustomizations,
				customizeByPlatform: isSplit,
				targetedPlatforms,
			}),
		[capabilities, isSplit, platformCustomizations, postContent, postImages, targetedPlatforms]
	);

	const activeCustomization = isSplit ? findPlatformCustomization(platformCustomizations, activePlatform) : undefined;
	const activeCapability = findPlatformCapabilities(capabilities, activePlatform);

	const isPlatformEdited = (platform: Api.SocialMediaType) =>
		customizationDiffersFromBase(findPlatformCustomization(platformCustomizations, platform), postContent, postImages);

	const discardedChanges = isSplit ? getPlatformVersionChanges(platformCustomizations, postContent, postImages) : [];
	const discardSpreadsMedia = isSplit && isMediaLeftOffAPlatform(platformCustomizations, postImages, targetedPlatforms);

	return {
		activeAttachments: isSplit ? (activeCustomization?.attachments ?? []) : (postImages ?? []),
		activeCapability,
		activeContent: isSplit ? (activeCustomization?.content ?? '') : (postContent ?? ''),
		// A problem with the shared media pool belongs to no one version, so it reads alongside
		// whichever one is on screen.
		activeErrors: activePlatform
			? [...getPostWideErrors(errors), ...getErrorsForPlatform(errors, activePlatform)]
			: getPostWideErrors(errors),
		activePlatform,
		capabilities,
		/**
		 * The ceiling the version on screen is held to: its own platform's, or the reduction when
		 * unsplit. Undefined until the API reports it.
		 */
		characterLimit: isSplit ? activeCapability?.maxContentCharacters : capabilities?.combined?.maxContentCharacters,
		canCustomize: enabled && targetedPlatforms.length > 0,
		/** What turning the split off takes from each platform, which the confirmation lists. */
		discardedChanges,
		/** Whether turning the split off publishes media on platforms whose versions left it out. */
		discardSpreadsMedia,
		errors,
		/** Anything that stops this post publishing. Gates the schedule and send controls. */
		hasBlockingErrors: errors.length > 0,
		/**
		 * The subset the API rejects any save for. A draft is savable while a publish-only error
		 * stands, but not while one of these does — per-platform content length and every media
		 * ceiling are checked on create and update alike.
		 */
		hasSaveBlockingErrors: getSaveBlockingErrors(errors).length > 0,
		isPlatformEdited,
		isSplit,
		// Post-wide problems are deliberately left out: they would dot every tab while belonging to
		// none of them.
		platformHasErrors: (platform: Api.SocialMediaType) => getErrorsForPlatform(errors, platform).length > 0,
		resetActiveToBase: () => resetPlatformCustomization(activePlatform),
		setActiveContent: (content: string) => setPlatformCustomization(activePlatform, { content }),
		setActivePlatform,
		setCustomizeByPlatform,
		targetedPlatforms,
		toggleActiveAttachment: (attachment: Api.IFileAttachment) =>
			setPlatformCustomization(activePlatform, current => ({
				attachments: current.attachments.some(routed => routed.id === attachment.id)
					? current.attachments.filter(routed => routed.id !== attachment.id)
					: (postImages ?? []).filter(
							image => image.id === attachment.id || current.attachments.some(routed => routed.id === image.id)
						),
			})),
	};
}

export type IPlatformCustomizationView = ReturnType<typeof usePlatformCustomization>;
